import qs from 'qs'

import type { ICursorPaginatedResponse } from '~/types/catalog'
import type { TStretchedDeviceType } from '~/types/device-type'
import type { IUaCampaign } from '~/types/ua-campaign'

import config from '~/config'
import { request } from '~/utils/request'

interface IUaCampaignFilters {
  appKeys: string[]
  campaignNames: string[]
  countries: string[]
  deviceType: TStretchedDeviceType
  endDate: string
  hideTreatedReco?: boolean
  networks: string[]
  startDate: string
}

/** @doc https://mwm-ua-manager-intranet-dev-530307631011.europe-west1.run.app/#get-/campaign-recommendation */
export const listCampaignRecommendations = (
  filters: IUaCampaignFilters & {
    adSetNames: string[]
  },
  nextPageCursor?: string
): Promise<ICursorPaginatedResponse<IUaCampaign>> => {
  const queryParams = {
    adset_name: filters.adSetNames,
    app_key: filters.appKeys,
    campaign_name: filters.campaignNames,
    country: filters.countries,
    device_type: filters.deviceType,
    end_date: filters.endDate,
    limit: 10,
    network: filters.networks,
    next_page_cursor: nextPageCursor,
    start_date: filters.startDate,
    treated: filters.hideTreatedReco ? false : undefined,
  }

  return request({
    params: queryParams,
    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    url: `${config.UA_CAMPAIGN}/v2/campaign-recommendation`,
  })
}

/** @doc https://mwm-ua-manager-intranet-dev-530307631011.europe-west1.run.app/#get-/adset-name */
export const listAdSetNames = (filters: IUaCampaignFilters): Promise<string[]> => {
  const queryParams = {
    app_key: filters.appKeys,
    campaign_name: filters.campaignNames,
    country: filters.countries,
    device_type: filters.deviceType,
    end_date: filters.endDate,
    network: filters.networks,
    start_date: filters.startDate,
    treated: filters.hideTreatedReco ? false : undefined,
  }

  return request({
    params: queryParams,
    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    url: `${config.UA_CAMPAIGN}/adset-name`,
  })
}

/** @doc https://mwm-ua-manager-intranet-dev-530307631011.europe-west1.run.app/#get-/campaign-name */
export const listCampaignNames = (filters: Omit<IUaCampaignFilters, 'campaignNames'>): Promise<string[]> => {
  const queryParams = {
    app_key: filters.appKeys,
    country: filters.countries,
    device_type: filters.deviceType,
    end_date: filters.endDate,
    network: filters.networks,
    start_date: filters.startDate,
    treated: filters.hideTreatedReco ? false : undefined,
  }

  return request({
    params: queryParams,
    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    url: `${config.UA_CAMPAIGN}/campaign-name`,
  })
}

/** @doc https://mwm-ua-manager-intranet-dev-530307631011.europe-west1.run.app/#put-/campaign-recommendation/treatment-status */
export const setTreatmentStatus = ({ id, treated }: { id: string; treated: boolean }): Promise<void> => {
  return request({
    data: { id, treated },
    method: 'PUT',
    url: `${config.UA_CAMPAIGN}/campaign-recommendation/treatment-status`,
  })
}

/** @doc https://mwm-ua-manager-intranet-dev-530307631011.europe-west1.run.app/#put-/campaign-recommendation/changes-status */
export const setChangesStatus = ({ changesMade, id }: { changesMade: boolean; id: string }): Promise<void> => {
  return request({
    data: { changes_made: changesMade, id },
    method: 'PUT',
    url: `${config.UA_CAMPAIGN}/campaign-recommendation/changes-status`,
  })
}

/** @doc https://mwm-ua-manager-intranet-dev-530307631011.europe-west1.run.app/#put-/campaign-recommendation/do-later */
export const setLaterStatus = ({ doLater, id }: { doLater: boolean; id: string }): Promise<void> => {
  return request({
    data: { do_later: doLater, id },
    method: 'PUT',
    url: `${config.UA_CAMPAIGN}/campaign-recommendation/do-later`,
  })
}

/** @doc https://mwm-ua-manager-intranet-dev-530307631011.europe-west1.run.app/#put-/campaign-recommendation/comment */
export const setComment = ({ comment, id }: { comment: string; id: string }): Promise<void> => {
  return request({
    data: { comment, id },
    method: 'PUT',
    url: `${config.UA_CAMPAIGN}/campaign-recommendation/comment`,
  })
}
